import { useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../../../components/common/buttons/Button';
import { MainHead } from '../../../components/common/head/MainHead';
import { CheckboxCircle } from '../../../components/common/inputs/CheckboxCircle';
import { TabPill } from '../../../components/common/tab/TabPill';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetProductOptionById } from '../../../hooks/api/product-option/queries/useGetProductOptionById.query';
import { useGetProductOptionsQuery } from '../../../hooks/api/product-option/queries/useGetProductOptions.query';
import { useGetProductByIdQuery } from '../../../hooks/api/product/queries/useGetProductById.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { IProductOption } from '../../../interfaces/Product.interfaces';

const MAIN_HEAD_HEIGHT = 40;
const TAB_HEIGHT = 56;

const BOTTOM_BUTTON_HEIGHT = 76;

const productOptionCategory = {
  outer: '실외',
  inner: '실내',
  functional: '기능',
  workingMachine: '작업기',
};

interface IProductOptionForDisplay extends IProductOption {
  isActived: boolean;
}

export const ProductOptionPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState<'ALL' | 'INCLUDED'>('ALL');

  const { data: allProductOptions } = useGetProductOptionsQuery();
  const { data: activedProductOptions } = useGetProductOptionById({ productId: productId! });
  const { data: productCategory } = useGetProductByIdQuery({
    productId: productId!,
    searchParam: { fields: 'category' },
  });

  const productOptionsForDisplay = useMemo<IProductOptionForDisplay[]>(() => {
    if (!allProductOptions) {
      return [];
    }

    if (!activedProductOptions) {
      return allProductOptions.map((productOption): IProductOptionForDisplay => {
        return { ...productOption, isActived: false };
      });
    }

    return allProductOptions.map((productOption): IProductOptionForDisplay => {
      let isProductOptionActived = false;

      activedProductOptions?.forEach((activedProductOption) => {
        if (activedProductOption.id === productOption.id) {
          isProductOptionActived = true;
        }
      });

      return { ...productOption, isActived: isProductOptionActived };
    });
  }, [allProductOptions, activedProductOptions]);

  const PDP_URL = `${pageInfo.productDetailPage.path}/${productId}?category=${productCategory}`;

  useLayout({
    fixedHeader: (
      <>
        <MainHead
          backButton={{
            isShow: true,
            directlyPassedURL: PDP_URL,
          }}
          headerTitle={'옵션 상세 보기'}
        />
        <TabPill
          tabInfos={[
            { name: '전체', isActived: selectedTab === 'ALL', handleClick: () => setSelectedTab('ALL') },
            {
              name: '매물에 포함된 것만',
              isActived: selectedTab === 'INCLUDED',
              handleClick: () => setSelectedTab('INCLUDED'),
            },
          ]}
        />
      </>
    ),
    fixedFooter: (
      <FixedBottomContainer>
        <BlurredSection />
        <Button
          borderType={'square'}
          color={'light'}
          size={'md'}
          text={{ size: 'md', content: '닫기' }}
          onClick={() => navigate(PDP_URL)}
        />
      </FixedBottomContainer>
    ),
    fixedHeaderHeight: MAIN_HEAD_HEIGHT + TAB_HEIGHT,
    fixedFooterHeight: BOTTOM_BUTTON_HEIGHT,
  });

  const outerOptions = productOptionsForDisplay?.filter(
    (productOption) => productOption.category === productOptionCategory.outer,
  );
  const innerOptions = productOptionsForDisplay?.filter(
    (productOption) => productOption.category === productOptionCategory.inner,
  );
  const functionalOptions = productOptionsForDisplay?.filter(
    (productOption) => productOption.category === productOptionCategory.functional,
  );
  const workingMachineOptions = productOptionsForDisplay?.filter(
    (productOption) => productOption.category === productOptionCategory.workingMachine,
  );

  const productOptionByCategory = {
    outer: outerOptions,
    inner: innerOptions,
    funtional: functionalOptions,
    workingMachine: workingMachineOptions,
  };

  return (
    <Container>
      {Object.entries(productOptionByCategory).map(([category, options], index) => {
        if (!options || options.length === 0) {
          return <></>;
        }

        return (
          <>
            <ProductOptionGroup key={category}>
              <TitleWrapper>
                <Title>{options[0].category}</Title>
                <TotalActivedOptionCount>{options.filter((option) => option.isActived).length}</TotalActivedOptionCount>
              </TitleWrapper>

              <ProductOptionContainer>
                {options
                  .filter((option) => {
                    if (selectedTab === 'ALL') {
                      return option;
                    }

                    return option.isActived === true;
                  })
                  .map((option) => (
                    <ProductOptionWrapper key={option.id}>
                      <ProductOptionContentsWrapper>
                        <CheckboxCircle isSelected={option.isActived} />
                        <ProductOptionDescription isActived={option.isActived}>
                          {option.description}
                        </ProductOptionDescription>
                      </ProductOptionContentsWrapper>
                    </ProductOptionWrapper>
                  ))}
              </ProductOptionContainer>
            </ProductOptionGroup>

            {index !== Object.entries(productOptionByCategory).length - 1 && <HorizontalLine />}
          </>
        );
      })}
    </Container>
  );
};

const FixedBottomContainer = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-medium, 8px);
  align-self: stretch;

  padding: 0px var(--radius-large, 16px) 32px var(--radius-large, 16px);

  background-color: white;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;
`;

const ProductOptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;

  padding: 32px var(--radius-large, 16px);
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const TotalActivedOptionCount = styled(Title)`
  color: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const ProductOptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  padding: 0px var(--radius-medium, 8px);
`;

const ProductOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  align-self: stretch;
`;

const ProductOptionContentsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const ProductOptionDescription = styled.span<{ isActived: boolean }>`
  color: ${(props) => (props.isActived ? '#232429' : '#d5d5e3')};

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const HorizontalLine = styled.hr`
  width: calc(100% - 32px);

  height: 0px;
  align-self: stretch;
  stroke-width: 1px;
  stroke: #e4e4ed;

  margin-left: 16px;
`;
